import cx from 'classnames';
import { useState, type VoidFunctionComponent } from 'react';

import { useQuery } from '../../../contexts/RouterContext';
import Icon, { icons } from '../../generic/Icon';
import Loader from '../../generic/Loader';
import { LoadingBar } from '../../generic/LoadingBar/LoadingBar';
import TooltipIcon from '../../generic/TooltipIcon';
import type { ReportsV3QueryParams } from '../types';

import { useLoadPreview, usePreviewURL } from './hooks';

import styles from './Preview.module.less';

/**
 * Renders the loading state for the preview.
 */
const PreviewLoading: VoidFunctionComponent = () => (
  <div className={styles.loading}>
    <Loader />
    <span className={styles.loadingText}>Your sample report is being generated...</span>
  </div>
);

type PreviewErrorProps = {
  message: string;
};

/**
 * Renders the error state for the preview when the iframe content
 * indicates a server error (e.g. 4xx/5xx response).
 */
const PreviewError: VoidFunctionComponent<PreviewErrorProps> = ({ message }) => (
  <div className={styles.error}>
    <Icon icon={icons.triangleExclamation} />
    <span className={styles.errorText}>{message}</span>
  </div>
);

/**
 * Renders the preview for the report. Really it's just an iframe which hits the
 * preview endpoint (which is what actually serves the HTML content for the report preview).
 * URL changes are debounced by useLoadPreview before the iframe navigates.
 */
type PreviewProps = {
  savedOptionsBypassRequestId?: number;
};

export const Preview: VoidFunctionComponent<PreviewProps> = ({ savedOptionsBypassRequestId = 0 }) => {
  const [query] = useQuery<ReportsV3QueryParams>();
  const flow = query.flow ?? 'standard';
  const isPDF = query.format === 'PDF';
  const previewURL = usePreviewURL();
  // Switching from custom back to standard skips the preview debounce, but only for the preview URL that the
  // switch produced; later URL changes are debounced as usual. Updated during render (rather than in an
  // effect) so the bypass applies to the same render as the flow change.
  const [lastFlowChange, setLastFlowChange] = useState({ flow, previousFlow: flow, previewURL });
  if (lastFlowChange.flow !== flow) {
    setLastFlowChange({ flow, previousFlow: lastFlowChange.flow, previewURL });
  }
  const shouldBypassDebounce = lastFlowChange.previousFlow === 'custom'
    && flow === 'standard'
    && lastFlowChange.previewURL === previewURL;
  const { cacheBustedURL, debouncedPreviewURL, isLoading, isPending, iframeError, onIframeLoad } = useLoadPreview(
    previewURL,
    shouldBypassDebounce,
    savedOptionsBypassRequestId,
  );

  const hasError = !isLoading && iframeError !== null;

  return (
    <div className={styles.preview}>
      <header className={styles.header}>
        <span className={styles.headerTitle}>Preview</span>
        <TooltipIcon
          id="report-preview-tooltip"
          icon={icons.circleQuestion}
          tooltip="Previews use sample data and only show a limited number of dependencies and licenses"
        />
      </header>
      <LoadingBar isVisible={isPending || isLoading} />
      {isLoading && <PreviewLoading />}
      {hasError && <PreviewError message={iframeError} />}
      <iframe
        key={cacheBustedURL}
        src={debouncedPreviewURL}
        title="report-preview"
        onLoad={onIframeLoad}
        // Note that we always render the iFrame (so that it loads), but we hide
        // it with display: none while the new content is loading. During the
        // pending (debounce) phase the old preview stays visible.
        className={cx(styles.iframe, (isLoading || hasError) && styles.iframeLoading)}
        // Chrome's built-in PDF viewer (PDFium) cannot render inside sandboxed
        // iframes regardless of which sandbox flags are set. Firefox's PDF.js
        // viewer is unaffected. We omit the sandbox attribute for PDF previews
        // so they display correctly in Chrome.
        sandbox={isPDF ? undefined : 'allow-same-origin allow-scripts'}
      />
    </div>
  );
};
